import type { CreateClientConfig } from "@/lib/api_client/client.gen";

const ADMIN_TOKEN_KEY = "backoffice_admin_token";
const ADMIN_TOKEN_ISSUED_AT_KEY = "backoffice_admin_token_issued_at";
// Backend mints admin/doctor tokens with a 4h life (TOKEN_EXPIRY_SECONDS).
// Refreshing at the halfway mark leaves 2h for the attempt to land or be
// retried — a threshold at the full lifetime would only ever fire on a token
// that has already expired, which the backend then refuses to renew.
const TOKEN_REFRESH_THRESHOLD_MS = 2 * 60 * 60 * 1000;

export const getApiBaseUrl = () => {
  // Inlined at bundle time — but only because build.ts lists it in
  // PUBLIC_ENV_VARS. An unlisted var survives bundling as a literal
  // `process.env.X` and throws "process is not defined" in the browser, so add
  // any new BUN_PUBLIC_* var there before reading it here.
  if (process.env.BUN_PUBLIC_API_URL) return process.env.BUN_PUBLIC_API_URL;
  const hostname = window?.location?.hostname;
  if (hostname?.includes("localhost")) return "http://localhost:3000";
  if (hostname?.includes("amino-dev")) return "https://api.amino-dev.akselerasjon.no";
  if (hostname?.startsWith("hetz.")) return "https://hetz.api.amino.no";
  return "https://api.amino.no";
};

export const createClientConfig: CreateClientConfig = (config) => ({
  ...config,
  baseUrl: getApiBaseUrl(),
});

// ── Admin token ─────────────────────────────────────────────

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token: string | null) => {
  try {
    if (token) {
      localStorage.setItem(ADMIN_TOKEN_KEY, token);
      localStorage.setItem(ADMIN_TOKEN_ISSUED_AT_KEY, String(Date.now()));
    } else {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(ADMIN_TOKEN_ISSUED_AT_KEY);
    }
  } catch {
    // ignore
  }
};

let refreshInFlight: Promise<void> | null = null;

// Plain fetch rather than the generated SDK on purpose: api_client/client.gen.ts
// imports createClientConfig from this module and calls it at module scope, so
// importing the SDK here would close that cycle and throw before the app boots.
function refreshToken(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;

  const token = getToken();
  if (!token) return Promise.resolve();

  refreshInFlight = fetch(`${getApiBaseUrl()}/api/authorization/refresh-token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(async (res) => {
      if (res.status === 401 || res.status === 403) {
        // Expired, or the role was revoked — nothing to renew. Reuse the same
        // event setup-interceptors.ts fires so this lands in the normal logout.
        window.dispatchEvent(new CustomEvent("auth:unauthorized"));
        return;
      }
      if (!res.ok) return;
      const body = (await res.json()) as { accessToken?: string };
      if (body.accessToken) setToken(body.accessToken);
    })
    .catch(() => {
      // Network error — keep the token and try again on a later request.
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

function refreshIfStale() {
  try {
    const issuedAtStr = localStorage.getItem(ADMIN_TOKEN_ISSUED_AT_KEY);
    if (!issuedAtStr) return;
    const issuedAt = parseInt(issuedAtStr, 10);
    if (Number.isNaN(issuedAt)) return;
    if (Date.now() - issuedAt > TOKEN_REFRESH_THRESHOLD_MS) void refreshToken();
  } catch {
    // localStorage unavailable — nothing to refresh against.
  }
}

export const getAuthHeaders = (): Record<string, string> => {
  const token = getToken();
  if (!token) return {};
  // Fire-and-forget so this stays synchronous for its ~185 call sites. The
  // current token is still valid for 2h, so the renewal lands well before any
  // request would fail on it — the next call picks the new one up.
  refreshIfStale();
  return { Authorization: `Bearer ${token}` };
};

/** Headers for a request that must go on the impersonation token rather than
 *  the admin one — patient health data, which the API answers only for a token
 *  bound to that patient (staffRead/staffWrite "none" in the backend's
 *  policies/index.ts). Not stale-refreshed: the token is minted per patient
 *  view and dies with it. */
export const impersonateHeaders = (
  token: string,
  base?: Record<string, string>,
): Record<string, string> => ({ ...base, Authorization: `Bearer ${token}` });

// ── Logout ──────────────────────────────────────────────────

export const clearAllTokens = () => {
  setToken(null);
  // Clean up legacy localStorage entries
  try { localStorage.removeItem("backoffice_impersonate_token"); } catch {}
  try { localStorage.removeItem("backoffice_impersonate_user_id"); } catch {}
};
